import React, { useState, useRef, useEffect } from "react";
import { Image } from "./image";
import { cn } from "@/lib/utils";

const VOD_ORIGIN = "https://vod-vcr.oss-cn-beijing.aliyuncs.com";
const MIUTA_ORIGIN = "https://sg-miuta-resource.oss-ap-southeast-1.aliyuncs.com";
const VOD_EFFECT_SG_ORIGIN = "https://vod-effect-sg-1300308946.cos.accelerate.myqcloud.com";

const toVodProxyUrl = (url: string): string => {
  if (!url || url.startsWith("/vod/")) return url;
  const isDev = typeof import.meta !== "undefined" && !!import.meta.env?.DEV;
  if (!isDev) return url;
  try {
    const u = new URL(url);
    if (u.origin === VOD_ORIGIN) {
      const path = u.pathname + (u.search || "");
      return `/vod${path}`;
    }
    if (u.origin === MIUTA_ORIGIN) {
      const path = u.pathname + (u.search || "");
      return `/miuta${path}`;
    }
    if (u.origin === VOD_EFFECT_SG_ORIGIN) {
      const path = u.pathname + (u.search || "");
      return `/vodeffectsg${path}`;
    }
    return url;
  } catch {
    return url;
  }
};

interface VideoProps
  extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, "poster"> {
  // 移除原生poster属性，使用自定义poster
  poster?: string;
  alt?: string;
}

export const Video = React.forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      poster,
      alt = "Video poster",
      className = "",
      onPlaying,
      onEnded,
      ...videoProps
    },
    ref
  ) => {
    const [showPoster, setShowPoster] = useState(Boolean(poster));
    const videoRef = useRef<HTMLVideoElement>(null);
    const posterRef = useRef<HTMLImageElement>(null);

    // 合并ref
    const mergedRef = (node: HTMLVideoElement) => {
      videoRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // 处理播放事件
    const handlePlaying = (e: React.SyntheticEvent<HTMLVideoElement>) => {
      setShowPoster(false);
      onPlaying?.(e);
    };

    // 处理视频结束
    const handleEnded = (e: React.SyntheticEvent<HTMLVideoElement>) => {
      setShowPoster(true);
      onEnded?.(e);
    };

    useEffect(() => {
      setShowPoster(Boolean(poster));
    }, [poster]);

    return (
      <div className="relative w-full h-full flex items-center justify-center">
        {/* 视频元素 */}
        <video
          ref={mergedRef}
          className={className}
          onPlaying={handlePlaying}
          onEnded={handleEnded}
          crossOrigin="anonymous"
          {...videoProps}
          src={videoProps?.src ? toVodProxyUrl(String(videoProps.src)) : videoProps?.src}
        />

        {/* 手动poster层 */}
        <Image
          ref={posterRef}
          src={poster}
          alt={alt}
          className={cn(
            "absolute pointer-events-none transition-opacity duration-300 flex-shrink-0",
            className,
            poster && showPoster ? "opacity-100" : "opacity-0"
          )}
        />
      </div>
    );
  }
);

// 设置displayName便于调试
Video.displayName = "Video";
